import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import moment from "moment";

import agent from "../../../agent";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import MenuItem from "@material-ui/core/MenuItem";
import Chip from "@material-ui/core/Chip";
import Input from "@material-ui/core/Input";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Select from "react-select";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import ClearIcon from "@material-ui/icons/Clear";
import CancelIcon from "@material-ui/icons/Cancel";

import ScheduleIcon from "@material-ui/icons/Schedule";
import NotesIcon from "@material-ui/icons/Notes";
import BuildIcon from "@material-ui/icons/Build";
import AssignmentIcon from "@material-ui/icons/Assignment";
import DescriptionIcon from "@material-ui/icons/Description";
import WarningIcon from "@material-ui/icons/Warning";
import InfoIcon from "@material-ui/icons/Info";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import MicIcon from "@material-ui/icons/Mic";
import PersonIcon from "@material-ui/icons/Person";

import HubTimepicker from "../../timepicker/HubTimepicker";
import RichTextEditor from "../../richTextEditor/RichTextEditor";
import "react-select/dist/react-select.css";
import "../../controls/MultiSelect.css";
import "./BulkEditModal.css";

class BulkSelectOption extends React.Component {
    handleClick = event => {
        this.props.onSelect(this.props.option, event);
    };

    render() {
        const { children, isFocused, isSelected, onFocus } = this.props;
        return (
            <MenuItem
                onFocus={onFocus}
                selected={isFocused}
                onClick={this.handleClick}
                component="div"
                style={{ fontWeight: isSelected ? 500 : 400 }}
            >
                {children}
            </MenuItem>
        );
    }
}

function BulkSelectWrapped(props) {
    const { ...other } = props;
    return (
        <Select
            optionComponent={BulkSelectOption}
            noResultsText={<Typography>{"No results found"}</Typography>}
            arrowRenderer={arrowProps => {
                return arrowProps.isOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />;
            }}
            clearRenderer={() => <ClearIcon />}
            valueComponent={valueProps => {
                const { value, children, onRemove } = valueProps;
                if (onRemove) {
                    return (
                        <Chip
                            tabIndex={-1}
                            label={children}
                            className="multi-select-chip"
                            deleteIcon={
                                <CancelIcon
                                    onTouchEnd={event => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        onRemove(value);
                                    }}
                                />
                            }
                            onDelete={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                onRemove(value);
                            }}
                        />
                    );
                }
                return <div className="Select-value">{children}</div>;
            }}
            {...other}
        />
    );
}

const EDITABLE_FIELDS = [
    { key: "times", label: "Times", description: "Change start/end times, load-in, and soundcheck", icon: ScheduleIcon },
    { key: "notes", label: "Notes", description: "Update booking notes visible to the act", icon: NotesIcon },
    { key: "productionNotes", label: "Production Requirements", description: "Update technical and production requirements", icon: BuildIcon },
    { key: "gigRequirements", label: "Gig Requirements", description: "Update dress code, set numbers, vibes and suggested genres", icon: AssignmentIcon },
    { key: "agentContract", label: "Agent Contract", description: "Update the agent contract terms", icon: DescriptionIcon },
    { key: "venueManager", label: "Venue Manager", description: "Update the venue manager / contact name", icon: PersonIcon }
];

const MAX_BOOKINGS_LIMIT = 50;

@inject("bookingRequestStore", "profileStore", "templateStore")
@observer
class BulkEditModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            activeStep: 0,
            selectedFields: [],
            editValues: {
                timeStart: null,
                timeEnd: null,
                loadInStart: null,
                loadInEnd: null,
                soundCheckStart: null,
                soundCheckEnd: null,
                notes: "",
                productionNotes: "",
                setNumber: "",
                dress: "",
                vibes: "",
                genres: "",
                agentContract: "",
                venueManager: ""
            },
            originalTimes: {
                timeStart: null,
                timeEnd: null,
                loadInStart: null,
                loadInEnd: null,
                soundCheckStart: null,
                soundCheckEnd: null
            },
            placeholders: {
                timeStart: "",
                timeEnd: "",
                loadInStart: "",
                loadInEnd: "",
                soundCheckStart: "",
                soundCheckEnd: "",
                notes: "",
                productionNotes: "",
                setNumber: "",
                dress: "",
                vibes: "",
                genres: "",
                agentContract: "",
                venueManager: ""
            },
            vibeOptions: [],
            genreOptions: [],
            isSubmitting: false,
            error: null,
            isLoadingDetails: false,
            fetchedBookingDetails: null,
            updateResults: null,
            submittedBookings: null
        };
    }

    componentDidMount() {
        agent.Vibe.getAll().then(result => {
            this.setState({
                vibeOptions: result.map(v => ({ value: v.id, label: v.name }))
            });
        });
        agent.Genre.getAll().then(result => {
            this.setState({
                genreOptions: result.map(g => ({ value: g.id, label: g.name }))
            });
        });
    }

    exceedsBookingLimit = () => {
        const { selectedBookings } = this.props;
        return selectedBookings && selectedBookings.length > MAX_BOOKINGS_LIMIT;
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.open !== this.props.open && this.props.open) {
            this.setState({
                activeStep: 0,
                selectedFields: [],
                editValues: {
                    timeStart: null,
                    timeEnd: null,
                    loadInStart: null,
                    loadInEnd: null,
                    soundCheckStart: null,
                    soundCheckEnd: null,
                    notes: "",
                    productionNotes: "",
                    setNumber: "",
                    dress: "",
                    vibes: "",
                    genres: "",
                    agentContract: "",
                    venueManager: ""
                },
                originalTimes: {
                    timeStart: null,
                    timeEnd: null,
                    loadInStart: null,
                    loadInEnd: null,
                    soundCheckStart: null,
                    soundCheckEnd: null
                },
                placeholders: {
                    timeStart: "",
                    timeEnd: "",
                    loadInStart: "",
                    loadInEnd: "",
                    soundCheckStart: "",
                    soundCheckEnd: "",
                    notes: "",
                    productionNotes: "",
                    setNumber: "",
                    dress: "",
                    vibes: "",
                    genres: "",
                    agentContract: "",
                    venueManager: ""
                },
                isSubmitting: false,
                error: null,
                isLoadingDetails: false,
                fetchedBookingDetails: null,
                updateResults: null,
                submittedBookings: null
            });
        }
    }

    fetchBookingDetails = async (bookings) => {
        const { currentProfile } = this.props.profileStore;
        if (!bookings || bookings.length === 0) return bookings;

        let fetchErrors = 0;
        try {
            const detailPromises = bookings.map(async (booking) => {
                try {
                    const bookingRequestId = booking.bookingRequestId || booking.bookingRequest?.id || booking.request?.id;
                    
                    let detail;
                    if (bookingRequestId) {
                        detail = await agent.Booking.getDetail(booking.id, bookingRequestId, currentProfile?.id);
                    } else {
                        detail = await agent.Booking.getSummary(booking.id, currentProfile?.id);
                    }
                    
                    return {
                        ...booking,
                        loadInStart: detail.loadInStart || detail.loadInTime || booking.loadInStart || booking.loadInTime,
                        loadInEnd: detail.loadInEnd || booking.loadInEnd,
                        soundCheckStart: detail.soundCheckStart || detail.soundCheckTime || booking.soundCheckStart || booking.soundCheckTime,
                        soundCheckEnd: detail.soundCheckEnd || booking.soundCheckEnd,
                        timeStart: detail.timeStart || booking.timeStart,
                        timeEnd: detail.timeEnd || booking.timeEnd
                    };
                } catch (err) {
                    console.warn(`Failed to fetch details for booking ${booking.id}:`, err);
                    fetchErrors++;
                    return booking;
                }
            });

            const results = await Promise.all(detailPromises);
            if (fetchErrors > 0) {
                if (fetchErrors === bookings.length) {
                    this.setState({ error: "Could not load booking details. Load-in and soundcheck times may be missing." });
                } else {
                    this.setState({ error: `Could not load details for ${fetchErrors} of ${bookings.length} bookings. Some time fields may be missing.` });
                }
            }
            return results;
        } catch (err) {
            console.error("Error fetching booking details:", err);
            this.setState({ error: "Failed to load booking details. Please try again." });
            return bookings;
        }
    };

    formatTimeToHHmm = (timeValue) => {
        if (!timeValue) return "";
        if (typeof timeValue === "string" && /^\d{1,2}:\d{2}$/.test(timeValue)) {
            const parts = timeValue.split(":");
            return parts[0].padStart(2, "0") + ":" + parts[1];
        }
        if (typeof timeValue === "string" && /^\d{1,2}:\d{2}:\d{2}$/.test(timeValue)) {
            const parts = timeValue.split(":");
            return parts[0].padStart(2, "0") + ":" + parts[1];
        }
        const timeMoment = moment(timeValue, ["H:mm:ss", "HH:mm:ss", "H:mm", "HH:mm", "h:mm A", "h:mm a", moment.ISO_8601]);
        if (timeMoment.isValid()) {
            return timeMoment.format("HH:mm");
        }
        return "";
    };

    parseTimeToMoment = (timeValue) => {
        if (!timeValue) return null;
        if (moment.isMoment(timeValue)) return timeValue;
        const timeMoment = moment(timeValue, ["H:mm:ss", "HH:mm:ss", "H:mm", "HH:mm", "h:mm A", "h:mm a", moment.ISO_8601]);
        if (timeMoment.isValid()) {
            return timeMoment;
        }
        return null;
    };

    getInitialValuesFromBookings = (selectedBookings, selectedFields) => {
        if (!selectedBookings || selectedBookings.length === 0) {
            return { values: {}, placeholders: {} };
        }

        const values = {};
        const placeholders = {};

        const checkFieldValue = (fieldName, getter) => {
            const allValues = selectedBookings.map(getter).filter(v => v !== null && v !== undefined && v !== "");
            if (allValues.length === 0) {
                return { value: "", placeholder: "" };
            }
            const firstValue = allValues[0];
            const allSame = allValues.every(v => v === firstValue);
            if (allSame) {
                return { value: firstValue, placeholder: "" };
            } else {
                return { value: "", placeholder: "Multiple values - leave empty to keep existing" };
            }
        };

        const checkTimeFieldValue = (fieldName, getter) => {
            const allValues = selectedBookings.map(getter).filter(v => v !== null && v !== undefined && v !== "");
            const formattedValues = allValues.map(v => this.formatTimeToHHmm(v)).filter(v => v !== "");
            if (formattedValues.length === 0) {
                return { value: "", placeholder: "" };
            }
            const firstValue = formattedValues[0];
            const allSame = formattedValues.every(v => v === firstValue);
            if (allSame) {
                return { value: firstValue, placeholder: "" };
            } else {
                return { value: "", placeholder: "Multiple values" };
            }
        };

        if (selectedFields.includes("times")) {
            const timeStartResult = checkTimeFieldValue("timeStart", b => b.timeStart);
            values.timeStart = timeStartResult.value ? this.parseTimeToMoment(timeStartResult.value) : null;
            placeholders.timeStart = timeStartResult.placeholder;

            const timeEndResult = checkTimeFieldValue("timeEnd", b => b.timeEnd);
            values.timeEnd = timeEndResult.value ? this.parseTimeToMoment(timeEndResult.value) : null;
            placeholders.timeEnd = timeEndResult.placeholder;

            const loadInStartResult = checkTimeFieldValue("loadInStart", b => b.loadInStart || b.loadInTime);
            values.loadInStart = loadInStartResult.value ? this.parseTimeToMoment(loadInStartResult.value) : null;
            placeholders.loadInStart = loadInStartResult.placeholder;

            const loadInEndResult = checkTimeFieldValue("loadInEnd", b => b.loadInEnd);
            values.loadInEnd = loadInEndResult.value ? this.parseTimeToMoment(loadInEndResult.value) : null;
            placeholders.loadInEnd = loadInEndResult.placeholder;

            const soundCheckStartResult = checkTimeFieldValue("soundCheckStart", b => b.soundCheckStart || b.soundCheckTime);
            values.soundCheckStart = soundCheckStartResult.value ? this.parseTimeToMoment(soundCheckStartResult.value) : null;
            placeholders.soundCheckStart = soundCheckStartResult.placeholder;

            const soundCheckEndResult = checkTimeFieldValue("soundCheckEnd", b => b.soundCheckEnd);
            values.soundCheckEnd = soundCheckEndResult.value ? this.parseTimeToMoment(soundCheckEndResult.value) : null;
            placeholders.soundCheckEnd = soundCheckEndResult.placeholder;
        }

        if (selectedFields.includes("notes")) {
            const notesResult = checkFieldValue("notes", b => b.notes);
            values.notes = notesResult.value;
            placeholders.notes = notesResult.placeholder;
        }

        if (selectedFields.includes("productionNotes")) {
            const prodNotesResult = checkFieldValue("productionNotes", b => b.productionNotes);
            values.productionNotes = prodNotesResult.value;
            placeholders.productionNotes = prodNotesResult.placeholder;
        }

        if (selectedFields.includes("gigRequirements")) {
            const setNumberResult = checkFieldValue("setNumber", b => b.setNumber);
            values.setNumber = setNumberResult.value;
            placeholders.setNumber = setNumberResult.placeholder;

            const dressResult = checkFieldValue("dress", b => b.dress);
            values.dress = dressResult.value;
            placeholders.dress = dressResult.placeholder;

            const vibesResult = checkFieldValue("vibes", b => {
                if (b.bookingVibes && b.bookingVibes.length > 0) {
                    return b.bookingVibes.map(v => v.vibeId).join(",");
                }
                return "";
            });
            values.vibes = vibesResult.value;
            placeholders.vibes = vibesResult.placeholder;

            const genresResult = checkFieldValue("genres", b => {
                if (b.bookingGenres && b.bookingGenres.length > 0) {
                    return b.bookingGenres.map(g => g.genreId).join(",");
                }
                return "";
            });
            values.genres = genresResult.value;
            placeholders.genres = genresResult.placeholder;
        }

        if (selectedFields.includes("agentContract")) {
            const agentContractResult = checkFieldValue("agentContract", b => b.agentContract);
            values.agentContract = agentContractResult.value;
            placeholders.agentContract = agentContractResult.placeholder;
        }

        if (selectedFields.includes("venueManager")) {
            const venueManagerResult = checkFieldValue("venueManager", b => b.venueContact);
            values.venueManager = venueManagerResult.value;
            placeholders.venueManager = venueManagerResult.placeholder;
        }

        return { values, placeholders };
    };

    getUniqueVenues = () => {
        const { selectedBookings } = this.props;
        if (!selectedBookings || selectedBookings.length === 0) return [];
        
        const venueMap = new Map();
        selectedBookings.forEach(booking => {
            const venueId = booking.venueId || booking.venue?.id;
            const venueName = booking.venueName || booking.venue?.profileName || "Unknown Venue";
            if (venueId && !venueMap.has(venueId)) {
                venueMap.set(venueId, venueName);
            }
        });
        return Array.from(venueMap.values());
    };

    handleFieldToggle = (fieldKey) => {
        const { selectedFields } = this.state;
        if (selectedFields.includes(fieldKey)) {
            this.setState({
                selectedFields: selectedFields.filter(f => f !== fieldKey)
            });
        } else {
            this.setState({
                selectedFields: [...selectedFields, fieldKey]
            });
        }
    };

    handleValueChange = (field, value) => {
        this.setState({
            editValues: {
                ...this.state.editValues,
                [field]: value
            }
        });
    };

    isRichTextEmpty = (html) => {
        if (!html) return true;
        const stripped = html
            .replace(/<[^>]*>/g, "")
            .replace(/&nbsp;/g, " ")
            .replace(/\u00a0/g, " ")
            .trim();
        return stripped === "";
    };

    handleRichTextChange = (field, value) => {
        // Normalise empty editor content (e.g. "<p></p>") to an empty string so
        // the existing truthiness-based bulk-apply / review logic keeps working
        // and we never overwrite existing content with a blank value.
        this.handleValueChange(field, this.isRichTextEmpty(value) ? "" : value);
    };

    handleTimeChange = (field, value) => {
        const { editValues, originalTimes } = this.state;
        const newEditValues = { ...editValues, [field]: value };

        // Auto-shift load-in and soundcheck times when performance start time changes
        if (field === "timeStart" && originalTimes.timeStart && value) {
            const parseToMoment = (val) => {
                if (!val) return null;
                if (moment.isMoment(val)) return val;
                return moment(val, ["HH:mm:ss", "HH:mm", "h:mm A", "h:mm a", moment.ISO_8601]);
            };

            const originalStart = parseToMoment(originalTimes.timeStart);
            const newStart = parseToMoment(value);

            if (originalStart && originalStart.isValid() && newStart && newStart.isValid()) {
                const diffMinutes = newStart.diff(originalStart, "minutes");

                if (diffMinutes !== 0) {
                    // Shift all other times by the same amount
                    const shiftTime = (timeVal) => {
                        if (!timeVal) return null;
                        const m = parseToMoment(timeVal);
                        if (!m || !m.isValid()) return null;
                        return m.clone().add(diffMinutes, "minutes");
                    };

                    if (originalTimes.loadInStart) {
                        newEditValues.loadInStart = shiftTime(originalTimes.loadInStart);
                    }
                    if (originalTimes.loadInEnd) {
                        newEditValues.loadInEnd = shiftTime(originalTimes.loadInEnd);
                    }
                    if (originalTimes.soundCheckStart) {
                        newEditValues.soundCheckStart = shiftTime(originalTimes.soundCheckStart);
                    }
                    if (originalTimes.soundCheckEnd) {
                        newEditValues.soundCheckEnd = shiftTime(originalTimes.soundCheckEnd);
                    }
                    if (originalTimes.timeEnd) {
                        newEditValues.timeEnd = shiftTime(originalTimes.timeEnd);
                    }
                }
            }
        }

        this.setState({ editValues: newEditValues });
    };

    handleNext = async () => {
        const { activeStep, selectedFields } = this.state;
        const { selectedBookings } = this.props;

        if (activeStep === 0) {
            let bookingsToUse = selectedBookings;
            
            if (selectedFields.includes("times")) {
                this.setState({ isLoadingDetails: true });
                try {
                    bookingsToUse = await this.fetchBookingDetails(selectedBookings);
                    this.setState({ fetchedBookingDetails: bookingsToUse });
                } catch (err) {
                    console.error("Error fetching booking details:", err);
                } finally {
                    this.setState({ isLoadingDetails: false });
                }
            }
            
            const { values, placeholders } = this.getInitialValuesFromBookings(bookingsToUse, selectedFields);
            this.setState({ 
                activeStep: activeStep + 1,
                editValues: {
                    ...this.state.editValues,
                    ...values
                },
                originalTimes: {
                    timeStart: values.timeStart || null,
                    timeEnd: values.timeEnd || null,
                    loadInStart: values.loadInStart || null,
                    loadInEnd: values.loadInEnd || null,
                    soundCheckStart: values.soundCheckStart || null,
                    soundCheckEnd: values.soundCheckEnd || null
                },
                placeholders: {
                    ...this.state.placeholders,
                    ...placeholders
                }
            });
        } else {
            this.setState({ activeStep: activeStep + 1 });
        }
    };

    handleBack = () => {
        this.setState({ activeStep: this.state.activeStep - 1 });
    };

    formatMomentToTimeString = (timeVal) => {
        if (!timeVal) return null;
        if (moment.isMoment(timeVal)) {
            return timeVal.format("HH:mm");
        }
        if (typeof timeVal === "string") {
            const parsed = moment(timeVal, ["HH:mm:ss", "HH:mm", "h:mm A", "h:mm a", moment.ISO_8601]);
            if (parsed.isValid()) {
                return parsed.format("HH:mm");
            }
            return timeVal;
        }
        return null;
    };

    handleSubmit = async () => {
        const { selectedBookings, onSubmit } = this.props;
        const { selectedFields, editValues, fetchedBookingDetails } = this.state;

        if (this.exceedsBookingLimit()) {
            this.setState({ 
                error: `Maximum ${MAX_BOOKINGS_LIMIT} bookings allowed per bulk update. Please select fewer bookings.`
            });
            return;
        }

        this.setState({ isSubmitting: true, error: null });

        try {
            const updates = {};
            
            if (selectedFields.includes("times")) {
                if (editValues.timeStart) updates.timeStart = this.formatMomentToTimeString(editValues.timeStart);
                if (editValues.timeEnd) updates.timeEnd = this.formatMomentToTimeString(editValues.timeEnd);
                if (editValues.loadInStart) updates.loadInStart = this.formatMomentToTimeString(editValues.loadInStart);
                if (editValues.loadInEnd) updates.loadInEnd = this.formatMomentToTimeString(editValues.loadInEnd);
                if (editValues.soundCheckStart) updates.soundCheckStart = this.formatMomentToTimeString(editValues.soundCheckStart);
                if (editValues.soundCheckEnd) updates.soundCheckEnd = this.formatMomentToTimeString(editValues.soundCheckEnd);
            }
            
            if (selectedFields.includes("notes") && editValues.notes) {
                updates.notes = editValues.notes;
            }
            
            if (selectedFields.includes("productionNotes") && editValues.productionNotes) {
                updates.requirements = editValues.productionNotes;
            }
            
            if (selectedFields.includes("gigRequirements")) {
                if (editValues.setNumber !== "" && editValues.setNumber !== undefined) {
                    updates.setNumber = editValues.setNumber;
                }
                if (editValues.dress) {
                    updates.dress = editValues.dress;
                }
                if (editValues.vibes) {
                    updates.vibes = editValues.vibes.split(",").map(vibeId => ({ id: vibeId }));
                }
                if (editValues.genres) {
                    updates.genres = editValues.genres.split(",").map(genreId => ({ id: genreId }));
                }
            }

            if (selectedFields.includes("agentContract") && editValues.agentContract) {
                updates.agentContract = editValues.agentContract;
            }

            if (selectedFields.includes("venueManager") && editValues.venueManager) {
                updates.venueContact = editValues.venueManager;
            }

            const bookingsToSubmit = fetchedBookingDetails || selectedBookings;
            
            if (onSubmit) {
                const result = await onSubmit(bookingsToSubmit, updates);
                
                if (result && result.success === false && result.error) {
                    this.setState({ 
                        isSubmitting: false, 
                        error: result.error
                    });
                } else if (result) {
                    const updated = Array.isArray(result.updated) ? result.updated : bookingsToSubmit.map(b => b.id);
                    const failed = Array.isArray(result.failed) ? result.failed : [];
                    this.setState({ 
                        isSubmitting: false,
                        activeStep: 3,
                        updateResults: { updated, failed },
                        submittedBookings: bookingsToSubmit
                    });
                } else {
                    this.setState({ 
                        isSubmitting: false,
                        activeStep: 3,
                        updateResults: {
                            updated: bookingsToSubmit.map(b => b.id),
                            failed: []
                        },
                        submittedBookings: bookingsToSubmit
                    });
                }
            }
        } catch (error) {
            this.setState({ 
                isSubmitting: false, 
                error: error.message || "Failed to update bookings" 
            });
        }
    };

    renderStepIndicators = () => {
        const { activeStep } = this.state;
        const steps = [1, 2, 3];

        return (
            <div className="bulk-edit-step-indicators">
                {steps.map((step, index) => (
                    <React.Fragment key={step}>
                        <div 
                            className={`bulk-edit-step-circle ${activeStep >= index ? 'active' : ''} ${activeStep === index ? 'current' : ''}`}
                        >
                            {step}
                        </div>
                        {index < steps.length - 1 && (
                            <div className={`bulk-edit-step-line ${activeStep > index ? 'active' : ''}`} />
                        )}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    renderMultiVenueWarning = () => {
        const venues = this.getUniqueVenues();
        if (venues.length <= 1) return null;

        return (
            <div className="bulk-edit-warning">
                <WarningIcon className="bulk-edit-warning-icon" />
                <div>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                        Multiple venues selected
                    </Typography>
                    <Typography variant="caption">
                        You have selected bookings from {venues.length} different venues: {venues.join(", ")}
                    </Typography>
                </div>
            </div>
        );
    };

    renderStepContent = (step) => {
        switch (step) {
            case 0:
                return this.renderFieldSelection();
            case 1:
                return this.renderValueEditors();
            case 2:
                return this.renderReviewConfirm();
            case 3:
                return this.renderResults();
            default:
                return null;
        }
    };

    renderResults = () => {
        const { updateResults, submittedBookings } = this.state;
        const { selectedBookings } = this.props;

        if (!updateResults) return null;

        const { updated, failed } = updateResults;
        const hasFailures = failed && failed.length > 0;
        const allFailed = updated.length === 0 && hasFailures;

        const bookingsData = submittedBookings || selectedBookings || [];

        const getBookingInfo = (bookingId) => {
            const booking = bookingsData.find(b => b.id === bookingId);
            if (booking) {
                return {
                    date: moment(booking.dateStart).format("DD/MM/YYYY"),
                    actName: booking.actName || booking.act?.actName || "N/A",
                    venueName: booking.venueName || booking.venue?.profileName || "N/A"
                };
            }
            return { date: "N/A", actName: "N/A", venueName: "N/A" };
        };

        return (
            <div className="bulk-edit-step-content">
                <div style={{ 
                    textAlign: 'center', 
                    padding: '16px 0',
                    marginBottom: 16
                }}>
                    {allFailed ? (
                        <>
                            <WarningIcon style={{ fontSize: 48, color: '#e65100', marginBottom: 8 }} />
                            <Typography variant="h6" style={{ color: '#e65100' }}>
                                Update Failed
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                None of the bookings could be updated
                            </Typography>
                        </>
                    ) : hasFailures ? (
                        <>
                            <InfoIcon style={{ fontSize: 48, color: '#f57c00', marginBottom: 8 }} />
                            <Typography variant="h6" style={{ color: '#2e7d32' }}>
                                Partially Updated
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {updated.length} booking{updated.length !== 1 ? 's' : ''} updated, {failed.length} failed
                            </Typography>
                        </>
                    ) : (
                        <>
                            <div style={{ 
                                width: 48, 
                                height: 48, 
                                borderRadius: '50%', 
                                backgroundColor: '#e8f5e9', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                margin: '0 auto 8px'
                            }}>
                                <span style={{ fontSize: 24, color: '#2e7d32' }}>&#10003;</span>
                            </div>
                            <Typography variant="h6" style={{ color: '#2e7d32' }}>
                                Successfully Updated
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {updated.length} booking{updated.length !== 1 ? 's' : ''} updated successfully
                            </Typography>
                        </>
                    )}
                </div>

                {hasFailures && (
                    <Paper elevation={1} style={{ padding: 16, marginBottom: 16, backgroundColor: '#fff3e0' }}>
                        <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8, color: '#e65100' }}>
                            Failed Updates ({failed.length})
                        </Typography>
                        <div style={{ maxHeight: 200, overflow: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Act</TableCell>
                                        <TableCell>Reason</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {failed.map((item, index) => {
                                        const info = getBookingInfo(item.bookingId);
                                        return (
                                            <TableRow key={item.bookingId || index}>
                                                <TableCell>{info.date}</TableCell>
                                                <TableCell>{info.actName}</TableCell>
                                                <TableCell style={{ color: '#e65100' }}>{item.reason}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </Paper>
                )}

                {updated.length > 0 && (
                    <Paper elevation={1} style={{ padding: 16, backgroundColor: '#e8f5e9' }}>
                        <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8, color: '#2e7d32' }}>
                            Successfully Updated ({updated.length})
                        </Typography>
                        <div style={{ maxHeight: 150, overflow: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Act</TableCell>
                                        <TableCell>Venue</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {updated.map((bookingId, index) => {
                                        const info = getBookingInfo(bookingId);
                                        return (
                                            <TableRow key={bookingId || index}>
                                                <TableCell>{info.date}</TableCell>
                                                <TableCell>{info.actName}</TableCell>
                                                <TableCell>{info.venueName}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </Paper>
                )}
            </div>
        );
    };

    renderFieldSelection = () => {
        const { selectedFields } = this.state;

        return (
            <div className="bulk-edit-step-content">
                <Typography variant="body1" className="bulk-edit-prompt">
                    What would you like to change for these bookings?
                </Typography>
                {this.renderMultiVenueWarning()}
                <div className="bulk-edit-field-list">
                    {EDITABLE_FIELDS.map(field => {
                        const IconComponent = field.icon;
                        const isSelected = selectedFields.includes(field.key);
                        return (
                            <div 
                                key={field.key} 
                                className={`bulk-edit-field-card ${isSelected ? 'selected' : ''}`}
                                onClick={() => this.handleFieldToggle(field.key)}
                            >
                                <div className="bulk-edit-field-icon">
                                    <IconComponent />
                                </div>
                                <div className="bulk-edit-field-content">
                                    <Typography variant="body1" className="bulk-edit-field-label">
                                        {field.label}
                                    </Typography>
                                    <Typography variant="caption" className="bulk-edit-field-description">
                                        {field.description}
                                    </Typography>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    renderValueEditors = () => {
        const { selectedFields, editValues, placeholders } = this.state;

        return (
            <div className="bulk-edit-step-content">
                <Typography variant="body1" className="bulk-edit-prompt">
                    Enter the new values for selected fields:
                </Typography>
                {this.renderMultiVenueWarning()}

                {selectedFields.includes("times") && (
                    <Paper className="bulk-edit-editor-section" elevation={1}>
                        {/* Performance Times Section */}
                        <div className="bulk-edit-time-section">
                            <div className="bulk-edit-time-section-header">
                                <ScheduleIcon />
                                <Typography variant="subtitle1" component="h6">Performance Times</Typography>
                            </div>
                            <Grid container spacing={16}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="textSecondary">Start Time</Typography>
                                    <HubTimepicker
                                        value={editValues.timeStart}
                                        onChange={(value) => this.handleTimeChange("timeStart", value)}
                                        placeholder={placeholders.timeStart || "Select time"}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="textSecondary">End Time</Typography>
                                    <HubTimepicker
                                        value={editValues.timeEnd}
                                        onChange={(value) => this.handleTimeChange("timeEnd", value)}
                                        start={editValues.timeStart}
                                        defaultOffset={3}
                                        placeholder={placeholders.timeEnd || "Select time"}
                                    />
                                </Grid>
                            </Grid>
                        </div>

                        {/* Info Banner */}
                        <div className="bulk-edit-info-banner">
                            <InfoIcon />
                            <span>Load-in and Soundcheck times will automatically shift by the same amount</span>
                        </div>

                        {/* Load-in Times Section */}
                        <div className="bulk-edit-time-section">
                            <div className="bulk-edit-time-section-header">
                                <LocalShippingIcon />
                                <Typography variant="subtitle1" component="h6">Load-in Times</Typography>
                            </div>
                            <Grid container spacing={16}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="textSecondary">Load-in Start</Typography>
                                    <HubTimepicker
                                        value={editValues.loadInStart}
                                        onChange={(value) => this.handleTimeChange("loadInStart", value)}
                                        placeholder={placeholders.loadInStart || "Select time"}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="textSecondary">Load-in End</Typography>
                                    <HubTimepicker
                                        value={editValues.loadInEnd}
                                        onChange={(value) => this.handleTimeChange("loadInEnd", value)}
                                        start={editValues.loadInStart}
                                        placeholder={placeholders.loadInEnd || "Select time"}
                                    />
                                </Grid>
                            </Grid>
                        </div>

                        {/* Soundcheck Times Section */}
                        <div className="bulk-edit-time-section">
                            <div className="bulk-edit-time-section-header">
                                <MicIcon />
                                <Typography variant="subtitle1" component="h6">Soundcheck Times</Typography>
                            </div>
                            <Grid container spacing={16}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="textSecondary">Soundcheck Start</Typography>
                                    <HubTimepicker
                                        value={editValues.soundCheckStart}
                                        onChange={(value) => this.handleTimeChange("soundCheckStart", value)}
                                        placeholder={placeholders.soundCheckStart || "Select time"}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="textSecondary">Soundcheck End</Typography>
                                    <HubTimepicker
                                        value={editValues.soundCheckEnd}
                                        onChange={(value) => this.handleTimeChange("soundCheckEnd", value)}
                                        start={editValues.soundCheckStart}
                                        placeholder={placeholders.soundCheckEnd || "Select time"}
                                    />
                                </Grid>
                            </Grid>
                        </div>
                    </Paper>
                )}

                {selectedFields.includes("notes") && (
                    <Paper className="bulk-edit-editor-section" elevation={1}>
                        <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                            Booking Notes
                        </Typography>
                        <div className="bulk-edit-rte-wrapper">
                            <RichTextEditor
                                id="bulk-edit-notes-rte"
                                value={editValues.notes}
                                onChange={(value) => this.handleRichTextChange("notes", value)}
                                placeholder={placeholders.notes || "Enter notes to apply to all selected bookings..."}
                            />
                        </div>
                        {placeholders.notes && (
                            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: "block" }}>
                                {placeholders.notes}
                            </Typography>
                        )}
                    </Paper>
                )}

                {selectedFields.includes("productionNotes") && (
                    <Paper className="bulk-edit-editor-section" elevation={1}>
                        <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                            Production Requirements
                        </Typography>
                        <div className="bulk-edit-rte-wrapper">
                            <RichTextEditor
                                id="bulk-edit-production-rte"
                                value={editValues.productionNotes}
                                onChange={(value) => this.handleRichTextChange("productionNotes", value)}
                                placeholder={placeholders.productionNotes || "Enter production requirements..."}
                            />
                        </div>
                        {placeholders.productionNotes && (
                            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: "block" }}>
                                {placeholders.productionNotes}
                            </Typography>
                        )}
                    </Paper>
                )}

                {selectedFields.includes("gigRequirements") && (
                    <Paper className="bulk-edit-editor-section" elevation={1}>
                        <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                            Gig Requirements
                        </Typography>
                        <Grid container spacing={8}>
                            <Grid item xs={12} lg={6}>
                                <TextField
                                    select
                                    label="Number of Sets"
                                    value={editValues.setNumber}
                                    onChange={(e) => this.handleValueChange("setNumber", e.target.value)}
                                    fullWidth
                                    helperText={placeholders.setNumber || ""}
                                >
                                    <MenuItem value="">
                                        <em>No change</em>
                                    </MenuItem>
                                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                                        <MenuItem key={n} value={n}>{n}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <TextField
                                    label="Dress"
                                    value={editValues.dress}
                                    onChange={(e) => this.handleValueChange("dress", e.target.value)}
                                    fullWidth
                                    placeholder={placeholders.dress || ""}
                                    helperText={placeholders.dress || ""}
                                />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <div style={{ flexGrow: 1, width: '100%', padding: '8px 0px 4px' }}>
                                    <FormControl className="fo" fullWidth>
                                        <InputLabel htmlFor="bulk-vibes" shrink={editValues.vibes !== ""}>
                                            Vibes
                                        </InputLabel>
                                        <Input
                                            id="bulk-vibes"
                                            fullWidth
                                            inputComponent={BulkSelectWrapped}
                                            inputProps={{
                                                value: editValues.vibes,
                                                multi: true,
                                                onChange: (val) => this.handleValueChange("vibes", val),
                                                placeholder: placeholders.vibes || "",
                                                instanceId: "bulk-vibes-select",
                                                id: "bulk-vibes-select",
                                                name: "bulk-vibes-select",
                                                simpleValue: true,
                                                options: this.state.vibeOptions
                                            }}
                                        />
                                    </FormControl>
                                </div>
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <div style={{ flexGrow: 1, width: '100%', padding: '8px 0px 4px' }}>
                                    <FormControl className="fo" fullWidth>
                                        <InputLabel htmlFor="bulk-genres" shrink={editValues.genres !== ""}>
                                            Genres
                                        </InputLabel>
                                        <Input
                                            id="bulk-genres"
                                            fullWidth
                                            inputComponent={BulkSelectWrapped}
                                            inputProps={{
                                                value: editValues.genres,
                                                multi: true,
                                                onChange: (val) => this.handleValueChange("genres", val),
                                                placeholder: placeholders.genres || "",
                                                instanceId: "bulk-genres-select",
                                                id: "bulk-genres-select",
                                                name: "bulk-genres-select",
                                                simpleValue: true,
                                                options: this.state.genreOptions
                                            }}
                                        />
                                    </FormControl>
                                </div>
                            </Grid>
                        </Grid>
                    </Paper>
                )}

                {selectedFields.includes("agentContract") && (
                    <Paper className="bulk-edit-editor-section" elevation={1}>
                        <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                            Agent Contract
                        </Typography>
                        <div className="bulk-edit-rte-wrapper">
                            <RichTextEditor
                                id="bulk-edit-agent-contract-rte"
                                value={editValues.agentContract}
                                onChange={(value) => this.handleRichTextChange("agentContract", value)}
                                placeholder={placeholders.agentContract || "Enter agent contract terms..."}
                            />
                        </div>
                        {placeholders.agentContract && (
                            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: "block" }}>
                                {placeholders.agentContract}
                            </Typography>
                        )}
                    </Paper>
                )}

                {selectedFields.includes("venueManager") && (
                    <Paper className="bulk-edit-editor-section" elevation={1}>
                        <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                            Venue Manager
                        </Typography>
                        <TextField
                            label="Venue Manager"
                            value={editValues.venueManager}
                            onChange={(e) => this.handleValueChange("venueManager", e.target.value)}
                            fullWidth
                            variant="outlined"
                            placeholder={placeholders.venueManager || "Enter venue manager / contact name..."}
                            helperText={placeholders.venueManager || ""}
                        />
                    </Paper>
                )}
            </div>
        );
    };

    renderReviewConfirm = () => {
        const { selectedBookings } = this.props;
        const { selectedFields, editValues, error } = this.state;

        return (
            <div className="bulk-edit-step-content">
                <Typography variant="body1" className="bulk-edit-prompt">
                    Review changes before applying:
                </Typography>
                {this.renderMultiVenueWarning()}

                <Paper className="bulk-edit-review-section" elevation={1}>
                    <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                        Changes to Apply
                    </Typography>
                    
                    {selectedFields.includes("times") && (
                        <div className="bulk-edit-review-item">
                            <Typography variant="body2" color="textSecondary">Performance Times:</Typography>
                            <ul style={{ margin: "4px 0", paddingLeft: 20 }}>
                                {editValues.timeStart && <li>Start Time: {moment.isMoment(editValues.timeStart) ? editValues.timeStart.format("h:mma") : editValues.timeStart}</li>}
                                {editValues.timeEnd && <li>End Time: {moment.isMoment(editValues.timeEnd) ? editValues.timeEnd.format("h:mma") : editValues.timeEnd}</li>}
                                {editValues.loadInTime && <li>Load-in Time: {moment.isMoment(editValues.loadInTime) ? editValues.loadInTime.format("h:mma") : editValues.loadInTime}</li>}
                                {editValues.soundCheckTime && <li>Soundcheck Time: {moment.isMoment(editValues.soundCheckTime) ? editValues.soundCheckTime.format("h:mma") : editValues.soundCheckTime}</li>}
                            </ul>
                        </div>
                    )}
                    
                    {selectedFields.includes("notes") && editValues.notes && (
                        <div className="bulk-edit-review-item">
                            <Typography variant="body2" color="textSecondary">Booking Notes:</Typography>
                            <div className="bulk-edit-review-richtext" dangerouslySetInnerHTML={{ __html: editValues.notes }} />
                        </div>
                    )}
                    
                    {selectedFields.includes("productionNotes") && editValues.productionNotes && (
                        <div className="bulk-edit-review-item">
                            <Typography variant="body2" color="textSecondary">Production Requirements:</Typography>
                            <div className="bulk-edit-review-richtext" dangerouslySetInnerHTML={{ __html: editValues.productionNotes }} />
                        </div>
                    )}
                    
                    {selectedFields.includes("gigRequirements") && (editValues.setNumber !== "" || editValues.dress || editValues.vibes || editValues.genres) && (
                        <div className="bulk-edit-review-item">
                            <Typography variant="body2" color="textSecondary">Gig Requirements:</Typography>
                            {editValues.setNumber !== "" && editValues.setNumber !== undefined && (
                                <Typography variant="body2">Number of Sets: {editValues.setNumber}</Typography>
                            )}
                            {editValues.dress && (
                                <Typography variant="body2">Dress: {editValues.dress}</Typography>
                            )}
                            {editValues.vibes && (
                                <Typography variant="body2">Vibes: {editValues.vibes.split(",").map(id => {
                                    const found = this.state.vibeOptions.find(v => String(v.value) === String(id));
                                    return found ? found.label : id;
                                }).join(", ")}</Typography>
                            )}
                            {editValues.genres && (
                                <Typography variant="body2">Genres: {editValues.genres.split(",").map(id => {
                                    const found = this.state.genreOptions.find(g => String(g.value) === String(id));
                                    return found ? found.label : id;
                                }).join(", ")}</Typography>
                            )}
                        </div>
                    )}

                    {selectedFields.includes("agentContract") && editValues.agentContract && (
                        <div className="bulk-edit-review-item">
                            <Typography variant="body2" color="textSecondary">Agent Contract:</Typography>
                            <div className="bulk-edit-review-richtext" dangerouslySetInnerHTML={{ __html: editValues.agentContract }} />
                        </div>
                    )}

                    {selectedFields.includes("venueManager") && editValues.venueManager && (
                        <div className="bulk-edit-review-item">
                            <Typography variant="body2" color="textSecondary">Venue Manager:</Typography>
                            <Typography variant="body2">{editValues.venueManager}</Typography>
                        </div>
                    )}
                </Paper>

                <Paper className="bulk-edit-review-section" elevation={1} style={{ marginTop: 16 }}>
                    <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                        Affected Bookings ({selectedBookings.length})
                    </Typography>
                    <div style={{ maxHeight: 200, overflow: "auto" }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Act</TableCell>
                                    <TableCell>Venue</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedBookings.map((booking, index) => (
                                    <TableRow key={booking.id || index}>
                                        <TableCell>
                                            {moment(booking.dateStart).format("DD/MM/YYYY")}
                                        </TableCell>
                                        <TableCell>{booking.actName || booking.act?.actName || "N/A"}</TableCell>
                                        <TableCell>{booking.venueName || booking.venue?.profileName || "N/A"}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </Paper>

                {error && (
                    <Typography color="error" style={{ marginTop: 16 }}>
                        {error}
                    </Typography>
                )}
            </div>
        );
    };

    canProceed = () => {
        const { activeStep, selectedFields, editValues } = this.state;
        
        switch (activeStep) {
            case 0:
                return selectedFields.length > 0;
            case 1:
                let hasAnyValue = false;
                if (selectedFields.includes("times")) {
                    if (editValues.timeStart || editValues.timeEnd || editValues.loadInTime || editValues.soundCheckTime) {
                        hasAnyValue = true;
                    }
                }
                if (selectedFields.includes("notes") && editValues.notes) hasAnyValue = true;
                if (selectedFields.includes("productionNotes") && editValues.productionNotes) hasAnyValue = true;
                if (selectedFields.includes("gigRequirements") && (editValues.setNumber !== "" || editValues.dress || editValues.vibes || editValues.genres)) hasAnyValue = true;
                if (selectedFields.includes("agentContract") && editValues.agentContract) hasAnyValue = true;
                if (selectedFields.includes("venueManager") && editValues.venueManager) hasAnyValue = true;
                return hasAnyValue;
            case 2:
                return true;
            default:
                return false;
        }
    };

    render() {
        const { open, onClose, selectedBookings } = this.props;
        const { activeStep, isSubmitting, isLoadingDetails } = this.state;

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                disableBackdropClick={isSubmitting || isLoadingDetails}
                disableEscapeKeyDown={isSubmitting || isLoadingDetails}
                className="bulk-edit-dialog"
            >
                <div className="bulk-edit-header">
                    <Typography variant="h6" className="bulk-edit-title" style={{ color: 'white' }}>
                        Bulk Edit Bookings
                    </Typography>
                    <Typography variant="body2" className="bulk-edit-subtitle" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                        {selectedBookings?.length || 0} bookings selected
                    </Typography>
                </div>

                <div className="bulk-edit-content">
                    {this.exceedsBookingLimit() && activeStep < 3 && (
                        <div className="bulk-edit-error" style={{ 
                            backgroundColor: '#ffebee', 
                            color: '#c62828', 
                            padding: '8px 12px', 
                            marginBottom: 12, 
                            borderRadius: 4,
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: 14
                        }}>
                            <WarningIcon style={{ marginRight: 8, fontSize: 18 }} />
                            Maximum {MAX_BOOKINGS_LIMIT} bookings allowed per bulk update. Please select fewer bookings.
                        </div>
                    )}
                    {this.state.error && (
                        <div className="bulk-edit-error" style={{ 
                            backgroundColor: '#fff3e0', 
                            color: '#e65100', 
                            padding: '8px 12px', 
                            marginBottom: 12, 
                            borderRadius: 4,
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: 14
                        }}>
                            <WarningIcon style={{ marginRight: 8, fontSize: 18 }} />
                            {this.state.error}
                        </div>
                    )}
                    {activeStep < 3 && this.renderStepIndicators()}
                    {this.renderStepContent(activeStep)}
                </div>

                <div className="bulk-edit-actions">
                    {activeStep === 3 ? (
                        <Button 
                            color="primary" 
                            variant="contained"
                            onClick={onClose}
                            className="bulk-edit-continue-btn"
                        >
                            Done
                        </Button>
                    ) : (
                        <>
                            <Button onClick={onClose} disabled={isSubmitting || isLoadingDetails} className="bulk-edit-cancel-btn">
                                Cancel
                            </Button>
                            {activeStep > 0 && (
                                <Button onClick={this.handleBack} disabled={isSubmitting || isLoadingDetails} className="bulk-edit-back-btn">
                                    Back
                                </Button>
                            )}
                            {activeStep < 2 ? (
                                <Button 
                                    color="primary" 
                                    variant="contained"
                                    onClick={this.handleNext}
                                    disabled={!this.canProceed() || isLoadingDetails || this.exceedsBookingLimit()}
                                    className="bulk-edit-continue-btn"
                                >
                                    {isLoadingDetails ? (
                                        <>
                                            <CircularProgress size={16} style={{ marginRight: 8, color: '#fff' }} />
                                            Loading...
                                        </>
                                    ) : (
                                        "Continue"
                                    )}
                                </Button>
                            ) : (
                                <Button 
                                    color="primary" 
                                    variant="contained"
                                    onClick={this.handleSubmit}
                                    disabled={isSubmitting || !this.canProceed() || this.exceedsBookingLimit()}
                                    className="bulk-edit-continue-btn"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <CircularProgress size={16} style={{ marginRight: 8, color: '#fff' }} />
                                            Updating...
                                        </>
                                    ) : (
                                        "Apply Changes"
                                    )}
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </Dialog>
        );
    }
}

export default BulkEditModal;
